<template>
  <div class="flex flex-col space-y-4">
    <div class="flex items-center">
      <span class="text-gray-700 font-medium py-2 sm:text-sm">{{
        t("admin.tasks.recurring.every")
      }}</span>
      <Interval
        class="ml-3"
        :value="interval"
        :disabled="disabled"
        @change="handleIntervalChanged"
      />
      <span class="text-gray-700 font-medium py-2 sm:text-sm ml-3">{{
        t("admin.tasks.recurring.year_years")
      }}</span>
    </div>
    <div>
      <input
        id="first"
        name="first"
        type="radio"
        :checked="onDayOrOnTheSelection === 0"
        @change="handleOnDayOrOnTheChanged(0)"
        :disabled="disabled"
        :class="disabled ? 'cursor-not-allowed opacity-60' : ''"
      />
      <div class="ml-2 inline-flex items-center">
        <span class="text-gray-700 font-medium py-2 sm:text-sm">{{
          t("admin.tasks.recurring.on")
        }}</span>
        <Months
          class="ml-3 mb-1 mt-0"
          @change="handleMonthChanged"
          :disabled="onDayOrOnTheSelection !== 0 || disabled"
          :value="month"
        />
        <Dates
          class="ml-3 mb-1 mt-0"
          @change="handleDateChanged"
          :month="month"
          :disabled="onDayOrOnTheSelection !== 0 || disabled"
          :value="date"
        />
      </div>
    </div>
    <div>
      <input
        type="radio"
        name="second"
        id="second"
        :checked="onDayOrOnTheSelection === 1"
        @change="handleOnDayOrOnTheChanged(1)"
        :disabled="disabled"
        :class="disabled ? 'cursor-not-allowed opacity-60' : ''"
      />
      <div class="ml-2 inline-flex items-center">
        <span class="text-gray-700 font-medium py-2 sm:text-sm text-no-wrap">{{
          t("admin.tasks.recurring.on_the")
        }}</span>
        <Position
          class="ml-3 mb-1 mt-0"
          @change="handlePositionChanged"
          :disabled="onDayOrOnTheSelection !== 1 || disabled"
          :value="position"
        />
        <Days
          class="ml-3 mb-1 mt-0"
          @change="handleDayChanged"
          :disabled="onDayOrOnTheSelection !== 1 || disabled"
          :value="day"
        />
        <span
          class="text-gray-700 font-medium py-2 sm:text-sm text-no-wrap ml-3"
          >{{ t("admin.tasks.recurring.of") }}</span
        >
        <Months
          class="ml-3 mb-1 mt-0"
          @change="handleOnDayOrOnTheSelectionMonthChanged"
          :disabled="onDayOrOnTheSelection !== 1 || disabled"
          :value="onDayOrOnTheSelectionMonth"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { RRule } from "rrule";
import Position from "@/Tailwind/Components/RRule/Components/Position";
import Dates from "@/Tailwind/Components/RRule/Components/Dates";
import Days from "@/Tailwind/Components/RRule/Components/Days";
import Months from "@/Tailwind/Components/RRule/Components/Months";
import { computed, onMounted, ref, defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import Interval from "@/Tailwind/Components/RRule/Components/Interval.vue";
const { t } = useI18n();

const emit = defineEmits(["change"]);
const props = defineProps({
  inputInterval: {
    type: Number,
    default: 1,
  },
  inputMonth: {
    type: Number,
    default: 1,
  },
  inputNWeekDay: {
    type: Object,
  },
  inputDate: {
    type: Number,
    default: 1,
  },
  inputOnDayOrOnSelection: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

onMounted(() => {
  emit("change", rule);
});

const interval = ref(props.inputInterval);
const month = ref(props.inputMonth || 1);
const onDayOrOnTheSelectionMonth = ref(props.inputMonth || 1);
const date = ref(props.inputDate);
const position = ref(props.inputNWeekDay?.n || 1);
const day = ref(props.inputNWeekDay?.weekDay || RRule.MO);
const onDayOrOnTheSelection = ref(props.inputOnDayOrOnSelection);

const rule = computed(() => {
  if (onDayOrOnTheSelection.value === 0) {
    return new RRule({
      freq: RRule.YEARLY,
      interval: interval.value,
      bymonth: month.value,
      bymonthday: date.value,
    });
  }

  return new RRule({
    freq: RRule.YEARLY,
    byweekday: day.value.nth(position.value),
    bymonth: onDayOrOnTheSelectionMonth.value,
  });
});

function handleIntervalChanged(v) {
  interval.value = v;
  emit("change", rule);
}

function handleMonthChanged(v) {
  month.value = parseInt(v);
  emit("change", rule);
}

function handleOnDayOrOnTheSelectionMonthChanged(v) {
  onDayOrOnTheSelectionMonth.value = parseInt(v);
  emit("change", rule);
}

function handleDateChanged(v) {
  date.value = v;
  emit("change", rule);
}

function handlePositionChanged(v) {
  position.value = v;
  emit("change", rule);
}

function handleDayChanged(v) {
  day.value = v;
  emit("change", rule);
}

function handleOnDayOrOnTheChanged(v) {
  onDayOrOnTheSelection.value = v;
  emit("change", rule);
}
</script>
